import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../../styles/auth/ResetPassword.css';
import { getRecaptchaToken, loadRecaptcha } from '../../utils/recaptcha';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Pre-load reCAPTCHA script when component mounts
        if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
            loadRecaptcha().catch(err => {
                console.warn('reCAPTCHA pre-load failed:', err);
            });
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        if (password !== confirmPassword) {
            setError('Passordene stemmer ikke overens');
            setLoading(false);
            return;
        }

        try {
            const token = searchParams.get('token');
            
            // Get reCAPTCHA token
            const recaptchaToken = await getRecaptchaToken('password_reset');
            
            if (!recaptchaToken && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
                setError('Kunne ikke verifisere. Vennligst prøv igjen.');
                setLoading(false);
                return;
            }
            
            // Include honeypot fields (should be empty - bots will fill these)
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
                token,
                password,
                recaptchaToken,
                website: '', // Honeypot field
                email_confirm: '', // Honeypot field
                _submission_time: Date.now().toString()
            });
            setSuccess('Passordet har blitt nullstilt');
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Feil ved nullstilling av passord');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="reset-password-container">
            <div className="reset-password-card">
                <h1>Nullstill passord</h1>
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="password">Nytt passord</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Bekreft passord</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>
                    {/* Honeypot fields - hidden, should be empty */}
                    <input
                        type="text"
                        name="website"
                        style={{display: 'none'}}
                        tabIndex="-1"
                        autoComplete="off"
                        value=""
                        readOnly
                    />
                    <input
                        type="text"
                        name="email_confirm"
                        style={{display: 'none'}}
                        tabIndex="-1"
                        autoComplete="off"
                        value=""
                        readOnly
                    />
                    <button type="submit" disabled={loading}>
                        {loading ? 'Nullstiller passord...' : 'Nullstill passord'}
                    </button>
                    {/* reCAPTCHA v3 badge - required by Google */}
                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                        <div className="recaptcha-notice">
                            <small>
                                This site is protected by reCAPTCHA and the Google{' '}
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                {' '}and{' '}
                                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                                {' '}apply.
                            </small>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ResetPassword; 